import { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Avatar,
  Typography,
  Row,
  Col,
  Descriptions,
  Space,
  Tag,
  message,
  Skeleton,
  Divider,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  SettingOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  KeyOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axios";
import Layout from "../../components/Layout";
import { getToken } from "../../utils/token";
import { channelController } from "../../controllers/channelController";
import { errorBlock } from "../../controllers/errorBlock";
import moment from "moment";
import {
  useGetSecretForHeadendMutation,
  useSetSecretForHeadendMutation,
} from "../../services/generalSetting.service";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Settings = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState({
    data: true,
    passwordSave: false,
    apiKeySave: false,
    headendSecret: false,
  });
  const [activeTab, setActiveTab] = useState("1");
  const [headendSecretData, setHeadendSecretData] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const { role } = useSelector((state) => state.userDetails);
  console.log(role, "roles");

  const [passwordForm] = Form.useForm();
  const [apiKeyForm] = Form.useForm();

  const [getHeadendSecret] = useGetSecretForHeadendMutation();
  const [setHeadendSecret] = useSetSecretForHeadendMutation();

  // Password change handler
  const handlePasswordChange = async (values) => {
    setLoading((prev) => ({ ...prev, passwordSave: true }));

    try {
      const res = await axios.post("/auth/resetpassword", {
        password: values.oldPassword,
        newpassword: values.newPassword,
        logoutofotherdevices: false,
      });

      const { response } = res.data;
      if (response === "success") {
        message.success("Password updated successfully");
        passwordForm.resetFields();
      } else {
        message.error("Failed to update password");
      }
    } catch (err) {
      errorBlock(err);
    } finally {
      setLoading((prev) => ({ ...prev, passwordSave: false }));
    }
  };

  // API Key update handler
  const handleApiKeyUpdate = async (values) => {
    setLoading((prev) => ({ ...prev, apiKeySave: true }));

    try {
      // Replace with your actual API endpoint
      const res = await axios.post("/api/company/update-api-key", {
        apiKey: values.apiKey,
        description: values.description,
      });

      if (res.data.success) {
        message.success("API key updated successfully");
        apiKeyForm.resetFields();
      } else {
        message.error("Failed to update API key");
      }
    } catch (err) {
      message.error("Error updating API key");
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, apiKeySave: false }));
    }
  };

  // Fetch headend secret
  const fetchHeadendSecret = async () => {
    try {
      const result = await getHeadendSecret({}).unwrap();
      if (result) {
        console.log(result, "result");
        setHeadendSecretData(result.secretkey);
      }
    } catch (err) {
      message.error("Failed to fetch headend secret");
    }
  };

  // Handle headend secret update
  const handleHeadendSecretUpdate = async () => {
    setLoading((prev) => ({ ...prev, headendSecret: true }));
    try {
      const result = await setHeadendSecret({}).unwrap();

      if (result.response === "success") {
        message.success("Headend secret updated successfully");
        fetchHeadendSecret();
      } else {
        message.error("Failed to update headend secret");
      }
    } catch (err) {
      message.error("Error updating headend secret");
    } finally {
      setLoading((prev) => ({ ...prev, headendSecret: false }));
    }
  };

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const username = await getToken("spiral_username");
        const res = await axios.post("/auth/useraccount/getuser", { username });
        const result = channelController(res);

        if (result.type !== "success") {
          message.error(result.message);
          return;
        }

        if (!result?.message.firstname || !result?.message.lastname) {
          message.warning("Profile information incomplete");
          return;
        }

        setProfile(result.message);
      } catch (err) {
        if (err.response) {
          message.error(err.response.data.response);
        } else {
          message.error(err.message);
        }
      } finally {
        setLoading((prev) => ({ ...prev, data: false }));
      }
    };

    fetchProfile();
  }, []);

  // Fetch headend secret when component mounts
  useEffect(() => {
    fetchHeadendSecret();
  }, []);

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <UserOutlined />
          Account
        </span>
      ),
      children: (
        <Card>
          <Row gutter={[24, 24]}>
            <Col span={24} style={{ textAlign: "center" }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: "#1890ff",
                  marginBottom: 16,
                }}
              />
              <Title level={3} style={{ margin: 0 }}>
                {loading.data ? (
                  <Skeleton.Input active size="small" />
                ) : (
                  `${profile?.firstname} ${profile?.lastname}`
                )}
              </Title>
              <Text type="secondary">
                {loading.data ? (
                  <Skeleton.Input active size="small" />
                ) : (
                  profile?.email
                )}
              </Text>
            </Col>

            <Col span={24}>
              <Divider />
              <Descriptions
                title="Profile Information"
                bordered
                column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
              >
                <Descriptions.Item label="Full Name">
                  {loading.data ? (
                    <Skeleton.Input active size="small" />
                  ) : (
                    `${profile?.firstname} ${profile?.lastname}`
                  )}
                </Descriptions.Item>

                <Descriptions.Item label="Username">
                  {loading.data ? (
                    <Skeleton.Input active size="small" />
                  ) : (
                    profile?.username
                  )}
                </Descriptions.Item>

                <Descriptions.Item label="Email">
                  {loading.data ? (
                    <Skeleton.Input active size="small" />
                  ) : (
                    profile?.email
                  )}
                </Descriptions.Item>

                <Descriptions.Item label="Status">
                  {loading.data ? (
                    <Skeleton.Input active size="small" />
                  ) : (
                    <Tag
                      color={
                        profile?.activestatus === "active" ? "green" : "red"
                      }
                    >
                      {profile?.activestatus?.toUpperCase()}
                    </Tag>
                  )}
                </Descriptions.Item>

                <Descriptions.Item label="Date Registered">
                  {loading.data ? (
                    <Skeleton.Input active size="small" />
                  ) : (
                    moment(profile?.dateregistered).format(
                      "MMMM Do YYYY, h:mm:ss a"
                    )
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <LockOutlined />
          Change Password
        </span>
      ),
      children: (
        <Card title="Change Password">
          <Row justify="center">
            <Col xs={24} sm={18} md={12} lg={10}>
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handlePasswordChange}
                autoComplete="off"
              >
                <Form.Item
                  label="Current Password"
                  name="oldPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your current password",
                    },
                    {
                      min: 6,
                      message: "Password must be at least 6 characters",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter current password"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label="New Password"
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your new password",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter new password"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm New Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm new password"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading.passwordSave}
                    size="large"
                    block
                  >
                    Update Password
                  </Button>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Card>
      ),
    },
    ...(role === "System Manager"
      ? [
          {
            key: "3",
            label: (
              <span>
                <SettingOutlined />
                Company Credentials
              </span>
            ),
            children: (
              <Card title="Company API Configuration">
                <Row justify="center">
                  <Col xs={24} sm={18} md={12} lg={10}>
                    <Card
                      title="Headend Secret Key"
                      extra={
                        <Button
                          type="text"
                          icon={
                            showSecret ? (
                              <EyeTwoTone />
                            ) : (
                              <EyeInvisibleOutlined />
                            )
                          }
                          onClick={() => setShowSecret(!showSecret)}
                        />
                      }
                    >
                      <div
                        style={{
                          padding: "16px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                          fontFamily: "monospace",
                          fontSize: "16px",
                          textAlign: "center",
                          wordBreak: "break-all",
                        }}
                      >
                        {showSecret
                          ? headendSecretData
                          : "••••••••••••••••••••••••••••••••"}
                      </div>
                    </Card>

                    <Space
                      direction="vertical"
                      style={{ width: "100%", marginTop: "24px" }}
                    >
                      <Button
                        type=""
                        loading={loading.headendSecret}
                        size="large"
                        block
                        icon={<KeyOutlined />}
                        className="!bg-black !text-white !outline-none"
                        onClick={handleHeadendSecretUpdate}
                      >
                        Generate New Secret Key
                      </Button>
                    </Space>

                    {headendSecretData && (
                      <Descriptions
                        bordered
                        column={1}
                        size="small"
                        style={{ marginTop: "24px" }}
                      >
                        <Descriptions.Item label="Last Updated">
                          {moment(headendSecretData.dateentered).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    )}

                    <Divider />

                    <div style={{ textAlign: "center" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        <strong>Security Notice:</strong> Keep your headend
                        secret key secure and never share it publicly. Rotate
                        keys regularly for enhanced security.
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            ),
          },
        ]
      : []),
  ];

  console.log(headendSecretData, "headendSecretData");
  return (
    <Layout>
      <div>
        <Card style={{ minHeight: "80vh" }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            size="large"
            items={tabItems}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default Settings;
